import type { Inbox } from '../../api/types';
import { isManagedInbox } from '../../lib/channelMeta';
import { useLoad } from '../../lib/useLoad';
import { useComms } from './context';

/**
 * Where this tenant is in getting campaigns going, read from its own state —
 * the Guide's steps and the first-visit checklist on the Campaigns home tick
 * from this one place, so the two can never disagree about whether a step is
 * done. Every tick is something real: a sender connected, a mailbox's records
 * checked, the website known and counted, links on the company's own domain.
 */
export function useCampaignSetup() {
  const { source, inboxes, capabilities } = useComms();
  const features = capabilities?.access.features;
  const analyticsOn = (features?.analytics ?? 'view') !== 'off';

  const tracking = useLoad(() => source.api.getCampaignLinkTracking(), [source]);
  const sites = useLoad(
    () => (analyticsOn ? source.api.listAnalyticsSites() : Promise.resolve([])),
    [source, analyticsOn],
  );
  const linkDomain = useLoad(() => source.api.getShortLinkDomain(), [source]);

  const mailboxes = inboxes.filter((inbox) => inbox.channelType === 'email');
  const numbers = inboxes.filter(
    (inbox) =>
      inbox.channelType === 'ringcentral' ||
      (inbox.channelType === 'twilio' && inbox.channel?.medium !== 'whatsapp'),
  );
  const senders: Inbox[] = [...mailboxes, ...numbers];
  const managedMailboxes = mailboxes.filter(isManagedInbox).flatMap((inbox) =>
    inbox.channelType === 'email' ? [{ inbox, health: inbox.channel.senderHealth }] : [],
  );
  const checkedMailbox = managedMailboxes.some(({ health }) => health?.level === 'ok');

  const hosts = tracking.data?.payload ?? [];
  const webInstalls = (sites.data ?? []).filter((site) => site.platform === 'web');
  const reporting = webInstalls.some((site) => site.health === 'ok');
  const websiteReady = hosts.length > 0 && webInstalls.length > 0;
  const domain = linkDomain.data?.domain ?? null;

  return {
    analyticsOn,
    tracking,
    sites,
    mailboxes,
    numbers,
    senders,
    managedMailboxes,
    checkedMailbox,
    hosts,
    webInstalls,
    reporting,
    websiteReady,
    /** The branded link domain, if one is set — `verified` is whether links use it yet. */
    linkDomain: domain,
    linkDomainLoaded: linkDomain.data !== null || Boolean(linkDomain.error),
  };
}
